import SupervisorService from "./SupervisorService"

export function makeDefaultState() {
    return {
        dashboard: {
            charts: [],
        },
        panel: {
            supervisorUrl: null,
            hasDashboard: false,
            hasCourses: false,
            hasParticipants: false,
            hasSupervisedEnrollments: false,
            hasFormationRequests: false,
            hasExternalFormationRequests: false,
            hasExternalExperienceRequests: false,
            hasPreregisterRequests: false,
        },
        counters: {
            coursesCount: 0,
            experiencePendingRequestsCount: 0,
            externalPendingRequestsCount: 0,
            lmsPendingRequestsCount: 0,
            pendingRequestsCount: 0,
            preregisterPendingRequestsCount: 0,
            supervisedCount: 0,
        },
    }
}

export const getters = {
    hasRequests: state => {
        const {
            hasFormationRequests,
            hasExternalFormationRequests,
            hasExternalExperienceRequests,
            hasPreregisterRequests,
        } = state.panel
        return (
            hasFormationRequests ||
            hasExternalFormationRequests ||
            hasExternalExperienceRequests ||
            hasPreregisterRequests
        )
    },

    links(state, getters) {
        const { hasDashboard, hasCourses, hasParticipants } = state.panel
        const { hasRequests } = getters
        const { pendingRequestsCount } = state.counters

        return [
            hasDashboard && {
                name: "dashboard",
                to: "/supervisor/dashboard",
                icon: "dashboard",
                notification: null,
            },
            hasCourses && {
                name: "courses",
                to: "/supervisor/courses",
                icon: "program",
                notification: null,
            },
            hasParticipants && {
                name: "participants",
                to: "/supervisor/participants",
                icon: "users-alt",
                notification: null,
            },
            hasRequests && {
                name: "requests",
                to: "/supervisor/requests",
                icon: "file-check",
                notification: pendingRequestsCount,
            },
        ].filter(Boolean)
    },

    availableRequestLinks(state) {
        const {
            hasFormationRequests,
            hasExternalFormationRequests,
            hasExternalExperienceRequests,
            hasPreregisterRequests,
        } = state.panel
        return [
            hasFormationRequests && "lms",
            hasExternalFormationRequests && "external",
            hasExternalExperienceRequests && "experience",
            hasPreregisterRequests && "preregister",
        ].filter(Boolean)
    },

    availableSections: (state, getters, rootState) => {
        const { hasDashboard, hasCourses, hasParticipants } = state.panel
        const { hasRequests } = getters
        const hasAccess = hasDashboard || hasCourses || hasParticipants
        const { hasMailbox } = rootState.platform
        const { sendMessageForbidden } = rootState.configs

        // These are route names
        return [
            hasAccess && "supervisor",
            hasDashboard && "dashboard",
            hasCourses && "courses",
            hasCourses && "coursesSection",
            hasCourses && "courseDetail",
            hasParticipants && "participantDetail",
            hasParticipants && "participants",
            hasRequests && "requests",
            hasRequests && "requestsList",
            hasRequests && "requestDetail",
            hasMailbox && "supervisorMessages",
            hasMailbox && sendMessageForbidden && "supervisorMessagesNew",
            hasMailbox && "supervisorMessageDetail",
        ].filter(Boolean)
    },
}

export const mutations = {
    clearModule(state) {
        state.dashboard.charts = []
        state.panel = {}
        state.counters = {}
    },

    setChart(state, { idx, chart }) {
        state.dashboard.charts[idx] = Object.assign(
            {},
            state.dashboard.charts[idx],
            chart
        )
    },

    setPanel(state, panel) {
        state.panel = panel
    },

    setCounters(state, counters) {
        state.counters = counters
    },
}

export function makeActions(service) {
    return {
        async init({ commit }) {
            const panel = await service.getPanel()
            commit("setPanel", panel)
        },

        getCounters({ commit }) {
            service.getCounters().then(counters => {
                commit("setCounters", counters)
            })
        },

        getDashboardElements({ commit }) {
            service.getDashboard().then(({ charts }) => {
                charts.forEach((chart, idx) => {
                    commit("setChart", {
                        chart: {
                            ...chart,
                            loading: true,
                        },
                        idx,
                    })

                    service.loadChart(chart).then(chart => {
                        commit("setChart", {
                            chart: {
                                ...chart,
                                loading: false,
                            },
                            idx,
                        })
                    })
                })
            })
        },

        initList({ commit, dispatch }, params) {
            const { type, url, title, presenter } = params
            const listId = params.listId || `supervisor-${type}s`
            const description = {
                cursor: null,
                listId,
                url,
                itemsType: `supervisor-${type}-card`,
                title,
                presenter,
            }
            commit("lists/setDescription", description, { root: true })
            dispatch(
                "lists/init",
                { key: listId, includeTotalCount: true },
                { root: true }
            )
        },

        send(
            { commit, dispatch },
            { includedUsers = [], excludedUsers = [], filters = [] }
        ) {
            commit("messages/clearNewMessage", null, { root: true })
            // TODO: currently, filters are not implemented so it only uses includedUsers
            dispatch(
                "messages/updateNewMessage",
                {
                    to: {
                        includedUsers,
                        excludedUsers,
                        filters,
                    },
                },
                { root: true }
            )
        },

        updateCounters({ state, commit, dispatch }, { type, payload }) {
            const { status, target } = payload
            if (!["OK", "KO"].includes(status)) {
                return
            }
            const { counters } = state
            const newCounters = Object.assign({}, counters)

            switch (type) {
                case "supervisor-lms":
                case "supervisor-external":
                case "supervisor-experience":
                case "supervisor-preregister": {
                    const counterKey = `${type.replace("supervisor-", "")}PendingRequestsCount`
                    newCounters[counterKey] -= target.total
                    break
                }

                default:
                    return
            }

            commit("setCounters", newCounters)
            dispatch("getCounters")
        },

        /**
         *
         * @param type String - list key: "supervisor-lms", "supervisor-external", "supervisor-experience", "supervisor-preregister"
         * @param payload Object - {
         *      status: "OK"/"KO"/"INFO",
         *      target: { included: Array, excluded: Array, filter: Array }
         * }
         */
        async editRequests({ dispatch }, { type, payload }) {
            try {
                dispatch("setLoadingElements", {
                    key: type,
                    payload,
                    loading: true,
                })
                await service.editRequests(type, payload)
                dispatch("updateCounters", { type, payload })
                // @todo if payload.target has only "included" items we don't have to reload the entire list
                dispatch("lists/justRefresh", { key: type }, { root: true })
            } catch (error) {
                dispatch(
                    "pushNotification",
                    {
                        title: error.message,
                        type: "error",
                    },
                    { root: true }
                )
            } finally {
                dispatch("setLoadingElements", {
                    key: type,
                    payload,
                    loading: false,
                })
            }
        },

        async download({ dispatch }, { type, payload, fileName }) {
            try {
                await service.download({
                    type,
                    payload,
                    fileName,
                    onNotification: title =>
                        dispatch("pushNotification", { title }, { root: true }),
                })
            } catch (error) {
                dispatch(
                    "pushNotification",
                    {
                        title: error.message,
                        type: "error",
                    },
                    { root: true }
                )
            }
        },

        async downloadMonitoringReport(
            { dispatch },
            { callFromDate, programs, unitBreakdown }
        ) {
            try {
                await service.downloadMonitoringReport({
                    callFromDate,
                    programs,
                    unitBreakdown,
                    onNotification: title =>
                        dispatch("pushNotification", { title }, { root: true }),
                })
            } catch (error) {
                dispatch(
                    "pushNotification",
                    {
                        title: error.message,
                        type: "error",
                    },
                    { root: true }
                )
            }
        },

        setLoadingElements({ commit, rootState }, { key, payload, loading }) {
            const { included } = payload.target
            if (!included.length) {
                // update all items from the list
                rootState.lists.instances[key].items.forEach(item => {
                    const newItem = {
                        ...item,
                        loading,
                    }
                    commit(
                        "lists/updateItem",
                        { item: newItem, listKey: key },
                        { root: true }
                    )
                })
                return
            }
            included.forEach(id => {
                const items = rootState.lists.instances[key].items
                const itemKey = items[0]._meta.key
                const oldItem = items.find(item => item[itemKey] == id)
                const newItem = {
                    ...oldItem,
                    loading,
                }
                commit(
                    "lists/updateItem",
                    { item: newItem, listKey: key },
                    { root: true }
                )
            })
        },
    }
}

export default {
    namespaced: true,
    state: makeDefaultState(),
    getters,
    mutations,
    actions: makeActions(new SupervisorService()),
}
